import { useEffect } from 'react';

/**
 * Пока открыто окно или меню, Radix блокирует прокрутку страницы, а резерв её полосы мы снимаем (index.css):
 * иначе оверлей оставил бы справа светлую полосу. Содержимое при этом расширялось бы на ширину резерва —
 * рывок. Radix гасит потерю полосы отступом `body`, но меряет её как `innerWidth − clientWidth`: пока резерв
 * есть, а видимой полосы нет (короткая страница, накладные или скрытые полосы), замер даёт 0 и отступа нет.
 * Поэтому ширину резерва меряем сами и докладываем на `html` ровно то, чего Radix не покрыл
 * (`--g-lock-pad`, `padding-right` — index.css): места в раскладке остаётся столько же, а оверлей
 * закрывает окно целиком. Работает на любой блокировке (окно, меню, поповер, ⌘K) — смотрит на
 * `data-scroll-locked`, который ставит Radix.
 *
 * Элементы `position: fixed` отступ `body` не двигает: их правый край — край окна без резерва, и без резерва он
 * уходил бы вправо. Для них на время блокировки выставляется `--g-lock-inset` (весь резерв), его читает
 * `right`/`margin-right` самого элемента (правая карточка — hybrid-panel.tsx).
 *
 * Ширина резерва меряется в покое (`ResizeObserver` на `html`: `innerWidth` минус его ширина), отступ Radix —
 * в момент блокировки: его стили и атрибут встают в одном проходе эффектов, раньше наблюдателя.
 */
export function useKeepGutterWhileLocked(): void {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const locked = () => body.hasAttribute('data-scroll-locked');
    const marginRight = () => Number.parseFloat(getComputedStyle(body).marginRight) || 0;
    let reserve = 0;
    let restMargin = 0;
    const measure = () => {
      if (locked()) return;
      reserve = Math.max(0, Math.round(window.innerWidth - html.getBoundingClientRect().width));
      restMargin = marginRight();
    };
    const setPx = (name: string, px: number) => {
      if (px > 0) html.style.setProperty(name, `${String(px)}px`);
      else html.style.removeProperty(name);
    };
    const sync = () => {
      const on = locked();
      setPx('--g-lock-pad', on ? reserve - (marginRight() - restMargin) : 0);
      setPx('--g-lock-inset', on ? reserve : 0);
    };
    measure();
    const resize = new ResizeObserver(measure);
    resize.observe(html);
    const mutation = new MutationObserver(sync);
    mutation.observe(body, { attributes: true, attributeFilter: ['data-scroll-locked'] });
    return () => {
      resize.disconnect();
      mutation.disconnect();
      html.style.removeProperty('--g-lock-pad');
      html.style.removeProperty('--g-lock-inset');
    };
  }, []);
}
